/**
 * BookingWizard — staff new-booking flow, three steps.
 *
 * URL-driven state: ?step=&customerId= preserves progress on reload.
 *
 *   1  Booking  — customer (search or create), service type, dates / slot
 *   2  Pets     — tick the pets that are coming; per pet: space, feeding,
 *                 medication, add-ons and the vaccine gate (record / override
 *                 inline); optional transport with the customer's address
 *                 pre-filled and the fee quoted from the tenant's settings
 *   3  Review   — summary, price (incl. transport), save / send to kiosk.
 *                 API rejections (vaccines, min-stay, assessment, space
 *                 clash) render as actionable panels here, not "422".
 *
 * Replaced the eight-step flow (customer → pets → service → dates →
 * assignments → transport → review → confirm), where three steps were
 * unconditionally skippable and the vaccine gate only surfaced as a raw
 * status code on the final step.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { CustomerCombobox } from '@/components/CustomerCombobox';
import { DatePicker } from '@/components/DatePicker';
import { PetMultiPicker } from '@/components/PetMultiPicker';
import { RunPicker } from '@/components/RunPicker';
import {
  VaccineRequirementsPanel,
  evaluateRequirements,
} from '@/components/VaccineRequirementsPanel';
import { What3WordsInput } from '@/components/What3WordsInput';
import {
  ApiError,
  type GroomingSlot,
  type TransportQuote,
  type Vaccine,
  type VaccineGateMissing,
  type VaccinePolicy,
  createBookingGroup,
  createGroomingAppointment,
  getCustomer,
  listGroomers,
  listGroomingSlots,
  listServices,
  listVaccinePolicies,
  listVaccines,
  listVehicles,
  postQuote,
  quoteTransport,
  sendGroupToKiosk,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type {
  Customer,
  Groomer,
  PetVaccinationSummary,
  PetWithVaccinations,
  Quote,
  ServiceCatalogueItem,
  Vehicle,
} from '@/lib/types';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { cn } from '@/lib/utils';
import { formatWhat3Words, normaliseWhat3Words, what3wordsMapUrl } from '@/lib/what3words';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Car,
  Check,
  Loader2,
  ReceiptText,
  Truck,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { RecurringDaycarePanel } from './recurring/RecurringDaycarePanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ServiceType = 'boarding' | 'daycare' | 'grooming';

interface PetSlot {
  petId: string;
  pet: PetWithVaccinations;
  runId: string | null;
  feeding: string;
  medication: string;
  selectedServiceIds: string[];
}

interface TransportConfig {
  enabled: boolean;
  direction: 'pickup' | 'dropoff' | 'both';
  vehicleId: string;
  pickupScheduledAt: string;
  dropoffScheduledAt: string;
  /** One address serves both legs — it is where the pet lives. */
  address: string;
  /** what3words square for the pickup point (raw text; normalised on send). */
  what3words: string;
  /** 'customer' = taken from the record; 'custom' = staff typed one. */
  addressSource: 'customer' | 'custom';
  /** 'auto' = fee from the quote; 'manual' = staff typed a fee. */
  feeMode: 'auto' | 'manual';
  manualFeeMinor: string;
  notes: string;
}

/** Structured API rejection surfaced on the review step. */
type SubmitFailure =
  | { kind: 'vaccines'; petId: string | null; missing: VaccineGateMissing[] }
  | { kind: 'run_unavailable'; petId: string | null }
  | { kind: 'run_species_mismatch'; petId: string | null; species: string | null }
  | { kind: 'min_stay'; message: string; overridable: boolean }
  | { kind: 'assessment'; message: string; overridable: boolean }
  | { kind: 'message'; message: string };

const emptyTransport = (): TransportConfig => ({
  enabled: false,
  direction: 'both',
  vehicleId: '',
  pickupScheduledAt: '',
  dropoffScheduledAt: '',
  address: '',
  what3words: '',
  addressSource: 'customer',
  feeMode: 'auto',
  manualFeeMinor: '',
  notes: '',
});

const STEP_LABELS = ['Booking', 'Pets', 'Review'];
const TOTAL_STEPS = STEP_LABELS.length;

/** The vaccine category the API gates on. Boarding + daycare both go through
 *  POST /api/bookings/group, which checks the 'boarding' policy. */
function gateCategory(serviceType: ServiceType): 'boarding' | 'grooming' {
  return serviceType === 'grooming' ? 'grooming' : 'boarding';
}

function hasCap(capabilities: readonly string[] | undefined, cap: string): boolean {
  const caps = capabilities ?? [];
  return caps.includes('*') || caps.includes(cap);
}

/** "12 High St, Glasgow, G1 1AA" from whatever the customer record holds. */
export function formatCustomerAddress(c: Customer | null): string {
  if (!c) return '';
  return [c.addressLine1, c.addressLine2, c.city, c.region, c.postcode]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

// ---------------------------------------------------------------------------
// Progress indicator
// ---------------------------------------------------------------------------

function StepIndicator({
  currentStep,
  onJump,
}: {
  currentStep: number;
  onJump: (step: number) => void;
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-0">
        {STEP_LABELS.map((label, i) => {
          const step = i + 1;
          const done = step < currentStep;
          const active = step === currentStep;
          return (
            <div key={label} className="flex flex-1 items-center">
              <button
                type="button"
                onClick={() => done && onJump(step)}
                disabled={!done}
                className="flex flex-col items-center disabled:cursor-default"
                aria-current={active ? 'step' : undefined}
              >
                <span
                  className={cn(
                    'grid size-7 place-items-center rounded-full text-xs font-medium ring-2',
                    done || active
                      ? 'bg-primary ring-primary text-primary-foreground'
                      : 'bg-background ring-border text-muted-foreground',
                  )}
                >
                  {done ? <Check className="size-3.5" strokeWidth={2.5} /> : step}
                </span>
                <span
                  className={cn(
                    'mt-1 text-[9px] font-medium uppercase tracking-[0.12em]',
                    active ? 'text-primary' : 'text-muted-foreground/60',
                  )}
                >
                  {label}
                </span>
              </button>
              {i < TOTAL_STEPS - 1 && (
                <div className={cn('mx-1 h-px flex-1', done ? 'bg-primary' : 'bg-border')} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

export function BookingWizard() {
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const facility = state.me?.activeFacility ?? state.me?.facilitiesInScope[0] ?? null;
  const capabilities = state.me?.capabilities;
  const canRecordVaccination = hasCap(capabilities, 'pets.vaccinations.verify');
  const canOverrideVaccine = hasCap(capabilities, 'vaccines.configure');
  const canOverrideMinStay = hasCap(capabilities, 'pricing.rules.write');
  const canOverrideAssessment = hasCap(capabilities, 'assessments.record');

  // Step (URL-driven for reload-safe progress)
  const step = Math.max(
    1,
    Math.min(TOTAL_STEPS, Number.parseInt(searchParams.get('step') ?? '1', 10) || 1),
  );
  const setStep = (s: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('step', String(s));
    setSearchParams(next, { replace: true });
  };

  // Wizard state
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [pets, setPets] = useState<PetSlot[]>([]);
  const [serviceType, setServiceType] = useState<ServiceType>('boarding');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkInTime, setCheckInTime] = useState('14:00');
  const [checkoutDate, setCheckoutDate] = useState('');
  const [checkoutTime, setCheckoutTime] = useState('10:00');
  const [transport, setTransport] = useState<TransportConfig>(emptyTransport());
  const [transportQuote, setTransportQuote] = useState<TransportQuote | null>(null);
  const [transportQuoteLoading, setTransportQuoteLoading] = useState(false);
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [policies, setPolicies] = useState<VaccinePolicy[]>([]);
  // Grooming-specific state
  const [groomingGroomerId, setGroomingGroomerId] = useState<string | null>(null);
  const [groomingStartAt, setGroomingStartAt] = useState(''); // ISO timestamp
  const [groomers, setGroomers] = useState<Groomer[]>([]);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitFailure, setSubmitFailure] = useState<SubmitFailure | null>(null);
  const [overrideMinStay, setOverrideMinStay] = useState(false);
  const [overrideAssessment, setOverrideAssessment] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Quote (computed on the review step — one quote per pet, totals summed)
  const [quote, setQuote] = useState<Quote | null>(null);
  const [perPetQuotes, setPerPetQuotes] = useState<Array<{ petName: string; quote: Quote }>>([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  // Pre-fill from URL: customer loading state
  const [customerPreloading, setCustomerPreloading] = useState(false);
  const [customerPreloadError, setCustomerPreloadError] = useState<string | null>(null);

  const facilityId = facility?.id ?? '';
  const accountId = state.me?.activeAccount?.id ?? '';
  const currency = state.me?.activeAccount?.currency ?? 'GBP';
  const category = gateCategory(serviceType);

  // URL-sync wrapper: when a customer is selected or cleared, keep ?customerId= in sync.
  const handleCustomerChange = (c: Customer | null) => {
    setCustomer(c);
    setPets([]);
    setSubmitFailure(null);
    setTransport((t) => ({ ...t, address: '', what3words: '', addressSource: 'customer' }));
    const next = new URLSearchParams(searchParams);
    if (c) next.set('customerId', c.id);
    else next.delete('customerId');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!facilityId) return;
    listServices(facilityId)
      .then(setServices)
      .catch(() => {});
    listVehicles()
      .then((r) => setVehicles(r.vehicles.filter((v) => v.isActive)))
      .catch(() => {});
    listGroomers()
      .then((r) => setGroomers(r.groomers.filter((g) => g.isActive === 1)))
      .catch(() => {});
    listVaccines()
      .then((r) => setVaccines(r.vaccines))
      .catch(() => {});
  }, [facilityId]);

  // Required-vaccine policies for the category the API will gate on.
  useEffect(() => {
    listVaccinePolicies(category)
      .then((r) => setPolicies(r.policies))
      .catch(() => setPolicies([]));
  }, [category]);

  // Pre-fill customer from ?customerId= on initial mount.
  useEffect(() => {
    const preloadId = searchParams.get('customerId');
    if (!preloadId || customer !== null) return;
    setCustomerPreloading(true);
    setCustomerPreloadError(null);
    getCustomer(preloadId)
      .then((loaded) => setCustomer(loaded))
      .catch(() => {
        setCustomerPreloadError('Customer not found. Please search for a customer below.');
      })
      .finally(() => setCustomerPreloading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only on initial mount

  const checkIn = checkInDate && checkInTime ? `${checkInDate}T${checkInTime}:00` : '';
  const checkout = checkoutDate && checkoutTime ? `${checkoutDate}T${checkoutTime}:00` : '';
  const visitDate = serviceType === 'grooming' ? groomingStartAt.slice(0, 10) : checkInDate;
  const primaryService = services.find((s) => s.category === serviceType && s.isActive === 1);

  // ---------------------------------------------------------------------------
  // Transport: address from the customer record, fee from the quote endpoint
  // ---------------------------------------------------------------------------

  const customerAddress = formatCustomerAddress(customer);
  const transportLegs: 1 | 2 = transport.direction === 'both' ? 2 : 1;
  const effectiveAddress =
    transport.addressSource === 'customer' ? customerAddress : transport.address;
  const customerWhat3words = normaliseWhat3Words(customer?.what3words) ?? '';
  const effectiveWhat3words =
    transport.addressSource === 'customer'
      ? customerWhat3words
      : (normaliseWhat3Words(transport.what3words) ?? '');

  // When transport is switched on, default the leg times to the stay
  // boundaries (staff can still edit them).
  const handleTransportChange = (t: TransportConfig) => {
    setSubmitFailure(null);
    if (t.enabled && !transport.enabled) {
      setTransport({
        ...t,
        pickupScheduledAt: t.pickupScheduledAt || checkIn.slice(0, 16),
        dropoffScheduledAt: t.dropoffScheduledAt || checkout.slice(0, 16),
      });
      return;
    }
    setTransport(t);
  };

  const quoteSeq = useRef(0);
  useEffect(() => {
    if (!transport.enabled || !facilityId || !customer) {
      setTransportQuote(null);
      return;
    }
    const seq = ++quoteSeq.current;
    setTransportQuoteLoading(true);
    const handle = setTimeout(() => {
      quoteTransport({
        facilityId,
        customerId: customer.id,
        ...(transport.addressSource === 'custom'
          ? {
              address: transport.address,
              ...(normaliseWhat3Words(transport.what3words)
                ? { what3words: normaliseWhat3Words(transport.what3words) }
                : {}),
            }
          : {}),
        legs: transportLegs,
      })
        .then((q) => {
          if (seq === quoteSeq.current) setTransportQuote(q);
        })
        .catch(() => {
          if (seq === quoteSeq.current) setTransportQuote(null);
        })
        .finally(() => {
          if (seq === quoteSeq.current) setTransportQuoteLoading(false);
        });
    }, 350);
    return () => clearTimeout(handle);
  }, [
    transport.enabled,
    transport.addressSource,
    transport.address,
    transport.what3words,
    transportLegs,
    facilityId,
    customer,
  ]);

  const transportFeeMinor = !transport.enabled
    ? 0
    : transport.feeMode === 'manual'
      ? Math.max(0, Number.parseInt(transport.manualFeeMinor || '0', 10) || 0)
      : (transportQuote?.feeMinor ?? 0);

  // ---------------------------------------------------------------------------
  // Price quote on the review step
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (step !== 3) return;
    if (!primaryService || !facilityId || !accountId || !checkInDate) {
      setQuote(null);
      setPerPetQuotes([]);
      return;
    }
    const endDate =
      serviceType === 'boarding' && checkoutDate
        ? checkoutDate
        : (() => {
            const d = new Date(`${checkInDate}T00:00:00Z`);
            d.setUTCDate(d.getUTCDate() + 1);
            return d.toISOString().slice(0, 10);
          })();
    if (serviceType === 'boarding' && !checkoutDate) {
      setQuote(null);
      setPerPetQuotes([]);
      return;
    }
    const petCount = pets.length || 1;
    setQuoteLoading(true);
    setQuoteError(null);
    const targets = pets.length > 0 ? pets : [null];
    Promise.all(
      targets.map((slot, idx) =>
        postQuote({
          accountId,
          facilityId,
          serviceId: primaryService.id,
          startDate: checkInDate,
          endDate,
          petCount,
          daycareGroupSize: petCount,
          daycareGroupIndex: idx + 1,
        }).then(({ quote: q }) => ({ petName: slot?.pet.name ?? `Pet ${idx + 1}`, quote: q })),
      ),
    )
      .then((results) => {
        const first = results[0];
        if (!first) return;
        setPerPetQuotes(results);
        const sum = (pick: (q: Quote) => number) => results.reduce((s, r) => s + pick(r.quote), 0);
        setQuote({
          base_minor: sum((q) => q.base_minor),
          modifiers: [],
          subtotal_minor: sum((q) => q.subtotal_minor),
          tax_minor: null,
          total_minor: sum((q) => q.total_minor),
          currency: first.quote.currency,
          payment_timing: first.quote.payment_timing,
          deposit_minor: sum((q) => q.deposit_minor),
          balance_minor: sum((q) => q.balance_minor),
          balance_due_at: first.quote.balance_due_at,
          cancellation_summary: first.quote.cancellation_summary,
        });
      })
      .catch(() => setQuoteError('Could not load price estimate'))
      .finally(() => setQuoteLoading(false));
  }, [step, primaryService, serviceType, checkInDate, checkoutDate, facilityId, accountId, pets]);

  // ---------------------------------------------------------------------------
  // Step gating
  // ---------------------------------------------------------------------------

  const datesValid =
    serviceType === 'grooming'
      ? Boolean(checkInDate) && Boolean(groomingStartAt)
      : serviceType === 'boarding'
        ? Boolean(checkIn) && Boolean(checkout) && checkout > checkIn
        : Boolean(checkIn);

  const canProceedFromStep = (s: number): boolean => {
    switch (s) {
      case 1:
        return customer !== null && datesValid;
      case 2:
        return pets.length > 0 && (serviceType !== 'grooming' || pets.length === 1);
      default:
        return true;
    }
  };

  const stepBlockedReason = (s: number): string | null => {
    if (s === 1) {
      if (!customer) return 'Choose a customer to continue.';
      if (!datesValid)
        return serviceType === 'grooming'
          ? 'Pick a date and an available slot.'
          : serviceType === 'boarding'
            ? 'Set check-in and checkout — checkout must be after check-in.'
            : 'Set the visit date.';
    }
    if (s === 2) {
      if (pets.length === 0) return 'Tick at least one pet.';
      if (serviceType === 'grooming' && pets.length > 1)
        return 'Grooming is booked one pet at a time.';
    }
    return null;
  };

  // ---------------------------------------------------------------------------
  // Pet slot actions
  // ---------------------------------------------------------------------------

  const togglePet = (pet: PetWithVaccinations) => {
    setSubmitFailure(null);
    setPets((arr) =>
      arr.some((p) => p.petId === pet.id)
        ? arr.filter((p) => p.petId !== pet.id)
        : [
            ...arr,
            {
              petId: pet.id,
              pet,
              runId: null,
              feeding: '',
              medication: '',
              selectedServiceIds: [],
            },
          ],
    );
  };
  const patchPet = (petId: string, patch: Partial<PetSlot>) =>
    setPets((arr) => arr.map((p) => (p.petId === petId ? { ...p, ...patch } : p)));

  const handleVaccinationRecorded = useCallback((petId: string, summary: PetVaccinationSummary) => {
    setPets((arr) =>
      arr.map((p) =>
        p.petId === petId
          ? { ...p, pet: { ...p.pet, vaccinations: [...p.pet.vaccinations, summary] } }
          : p,
      ),
    );
    // The server's complaint about this vaccine is now stale.
    setSubmitFailure((f) =>
      f && f.kind === 'vaccines'
        ? (() => {
            const missing = f.missing.filter((m) => m.vaccineId !== summary.vaccineId);
            return missing.length > 0 ? { ...f, missing } : null;
          })()
        : f,
    );
  }, []);

  const handleOverrideGranted = useCallback((_petId: string, vaccineId: string) => {
    setSubmitFailure((f) =>
      f && f.kind === 'vaccines'
        ? (() => {
            const missing = f.missing.filter((m) => m.vaccineId !== vaccineId);
            return missing.length > 0 ? { ...f, missing } : null;
          })()
        : f,
    );
  }, []);

  /** Client-side mirror of the gate — pets that will be rejected as things stand. */
  const petsFailingVaccines = useMemo(
    () =>
      pets.filter((p) =>
        evaluateRequirements({ pet: p.pet, policies, vaccines, visitDate }).some(
          (r) => r.status === 'not_recorded' || r.status === 'expired',
        ),
      ),
    [pets, policies, vaccines, visitDate],
  );

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const describeFailure = (e: unknown): SubmitFailure => {
    if (!(e instanceof ApiError)) {
      return { kind: 'message', message: (e as Error).message ?? 'Failed to save booking' };
    }
    const body = (e.body ?? {}) as Record<string, unknown>;
    switch (e.code) {
      case 'vaccines_required':
        return {
          kind: 'vaccines',
          petId: typeof body.petId === 'string' ? body.petId : null,
          missing: Array.isArray(body.missing) ? (body.missing as VaccineGateMissing[]) : [],
        };
      case 'run_unavailable':
        return {
          kind: 'run_unavailable',
          petId: typeof body.petId === 'string' ? body.petId : null,
        };
      case 'run_species_mismatch':
        return {
          kind: 'run_species_mismatch',
          petId: typeof body.petId === 'string' ? body.petId : null,
          species: typeof body.species === 'string' ? body.species : null,
        };
      case 'min_stay_not_met':
        return {
          kind: 'min_stay',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'The chosen dates are shorter than the minimum stay for this period.',
          overridable: body.overridable === true && canOverrideMinStay,
        };
      case 'assessment_required':
        return {
          kind: 'assessment',
          message:
            typeof body.message === 'string'
              ? body.message
              : 'A pet on this booking needs an evaluation day first.',
          overridable: body.overridable === true && canOverrideAssessment,
        };
      case 'capacity_full':
        return { kind: 'message', message: 'The facility is full on those dates.' };
      case 'pet_not_owned_by_customer':
        return {
          kind: 'message',
          message: 'One of the pets belongs to a different customer. Go back and re-select.',
        };
      case 'slot_unavailable':
        return {
          kind: 'message',
          message: 'That grooming slot has just been taken. Go back and pick another.',
        };
      case 'forbidden':
        return { kind: 'message', message: 'You do not have permission to create bookings.' };
      default:
        return {
          kind: 'message',
          message:
            typeof body.message === 'string' ? body.message : `Failed to save booking (${e.code}).`,
        };
    }
  };

  const submit = async (sendToKiosk: boolean) => {
    if (!customer || !facility) return;
    setSubmitting(true);
    setSubmitFailure(null);
    try {
      if (serviceType === 'grooming') {
        const firstPet = pets[0];
        if (!firstPet || !groomingStartAt) {
          setSubmitFailure({ kind: 'message', message: 'Select a grooming slot to continue.' });
          return;
        }
        await createGroomingAppointment({
          customerId: customer.id,
          petId: firstPet.petId,
          facilityId: facility.id,
          groomerId: groomingGroomerId,
          startAt: groomingStartAt,
          serviceIds: primaryService ? [primaryService.id] : [],
          notes: firstPet.feeding || null,
        });
        setSubmitted(true);
        navigate(`/customers/${customer.id}`);
        return;
      }

      const petCount = pets.length || 1;
      // Distribute the quote total evenly across pets; remainder to the last pet.
      const perPetTotal = quote ? Math.floor(quote.total_minor / petCount) : null;
      const lastPetExtra = quote ? quote.total_minor - (perPetTotal ?? 0) * petCount : 0;

      // One explicit entry per leg. A manual fee is a TOTAL, so it rides on
      // the first leg only (the server charges each leg's fee once); with no
      // fee anywhere the server prices every leg from the account settings
      // and the distance we resolved via /api/transport/quote.
      const legDirections: Array<'pickup' | 'dropoff'> =
        transport.direction === 'both' ? ['pickup', 'dropoff'] : [transport.direction];
      const transportBody = transport.enabled
        ? legDirections.map((direction, i) => ({
            direction,
            vehicleId: transport.vehicleId || null,
            scheduledAt:
              (direction === 'pickup'
                ? transport.pickupScheduledAt
                : transport.dropoffScheduledAt) || null,
            pickupAddress: effectiveAddress || null,
            pickupWhat3words: effectiveWhat3words || null,
            pickupLat: transportQuote?.destination.point?.lat ?? null,
            pickupLng: transportQuote?.destination.point?.lng ?? null,
            distanceMiles: transportQuote?.distanceMiles ?? null,
            feeMinor: transport.feeMode === 'manual' && i === 0 ? transportFeeMinor : null,
            notes: transport.notes || null,
          }))
        : null;

      const body = {
        customerId: customer.id,
        facilityId: facility.id,
        checkIn,
        checkout,
        pets: pets.map((p, idx) => ({
          petId: p.petId,
          runId: p.runId,
          feeding: p.feeding || null,
          medication: p.medication || null,
          totalMinor:
            perPetTotal !== null
              ? perPetTotal + (idx === pets.length - 1 ? lastPetExtra : 0)
              : undefined,
          serviceLineItems: p.selectedServiceIds.map((serviceId) => {
            const svc = services.find((s) => s.id === serviceId);
            return { serviceId, scheduledDate: null, priceMinor: svc?.defaultPriceMinor ?? 0 };
          }),
        })),
        transport: transportBody,
        ...(overrideMinStay ? { overrideMinStay: true } : {}),
        ...(overrideAssessment ? { overrideAssessment: true } : {}),
      };
      const { groupId } = await createBookingGroup(body);
      if (sendToKiosk) await sendGroupToKiosk(groupId);
      setSubmitted(true);
      navigate(`/customers/${customer.id}`);
    } catch (e: unknown) {
      setSubmitFailure(describeFailure(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!facility) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <p className="text-muted-foreground">No facility selected.</p>
      </div>
    );
  }

  if (customerPreloading) {
    return (
      <div className="mx-auto max-w-[860px] px-6 py-10 md:px-10 md:py-12">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
          <span className="text-sm">Loading customer…</span>
        </div>
      </div>
    );
  }

  const blocked = stepBlockedReason(step);

  return (
    <div className="mx-auto max-w-[860px] px-4 py-8 sm:px-6 md:px-10 md:py-12">
      <header className="mb-6">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
          Records · New booking
        </p>
        <h1 className="font-heading text-3xl font-medium tracking-tight md:text-4xl">
          New booking
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{facility.name}</p>
      </header>

      <StepIndicator currentStep={step} onJump={setStep} />

      <div className="rounded-2xl bg-surface p-5 ring-1 ring-border elevate md:p-8">
        {step === 1 && (
          <Step1Booking
            customer={customer}
            onCustomerChange={handleCustomerChange}
            preloadError={customerPreloadError}
            serviceType={serviceType}
            onServiceTypeChange={(t) => {
              setServiceType(t);
              setSubmitFailure(null);
              if (t !== 'grooming') {
                setGroomingStartAt('');
                setGroomingGroomerId(null);
              }
            }}
            checkInDate={checkInDate}
            checkInTime={checkInTime}
            checkoutDate={checkoutDate}
            checkoutTime={checkoutTime}
            onCheckInDateChange={(v) => {
              setCheckInDate(v);
              if (serviceType === 'grooming') {
                setGroomingStartAt('');
                setGroomingGroomerId(null);
              }
            }}
            onCheckInTimeChange={setCheckInTime}
            onCheckoutDateChange={setCheckoutDate}
            onCheckoutTimeChange={setCheckoutTime}
            facilityId={facilityId}
            services={services}
            groomers={groomers}
            groomingGroomerId={groomingGroomerId}
            groomingStartAt={groomingStartAt}
            onGroomingSlotSelect={(groomerId, startAt) => {
              setGroomingGroomerId(groomerId);
              setGroomingStartAt(startAt);
            }}
          />
        )}
        {step === 2 && (
          <Step2Pets
            customer={customer}
            pets={pets}
            onTogglePet={togglePet}
            onPatchPet={patchPet}
            serviceType={serviceType}
            services={services}
            facilityId={facilityId}
            checkIn={checkIn}
            checkout={checkout}
            visitDate={visitDate}
            groomingGroomerId={groomingGroomerId}
            groomers={groomers}
            vaccines={vaccines}
            policies={policies}
            category={category}
            canRecordVaccination={canRecordVaccination}
            canOverrideVaccine={canOverrideVaccine}
            onVaccinationRecorded={handleVaccinationRecorded}
            onOverrideGranted={handleOverrideGranted}
            transport={transport}
            onTransportChange={handleTransportChange}
            transportQuote={transportQuote}
            transportQuoteLoading={transportQuoteLoading}
            transportFeeMinor={transportFeeMinor}
            customerAddress={customerAddress}
            customerWhat3words={customerWhat3words}
            vehicles={vehicles}
            currency={currency}
            facilityCountry={facility.country ?? null}
          />
        )}
        {step === 3 && (
          <Step3Review
            customer={customer}
            pets={pets}
            serviceType={serviceType}
            primaryService={primaryService}
            checkIn={checkIn}
            checkout={checkout}
            transport={transport}
            transportAddress={effectiveAddress}
            transportWhat3words={effectiveWhat3words}
            transportQuote={transportQuote}
            transportFeeMinor={transportFeeMinor}
            quote={quote}
            perPetQuotes={perPetQuotes}
            quoteLoading={quoteLoading}
            quoteError={quoteError}
            currency={currency}
            groomingStartAt={groomingStartAt}
            groomingGroomerId={groomingGroomerId}
            groomers={groomers}
            submitting={submitting}
            submitted={submitted}
            failure={submitFailure}
            petsFailingVaccines={petsFailingVaccines}
            vaccines={vaccines}
            policies={policies}
            category={category}
            visitDate={visitDate}
            canRecordVaccination={canRecordVaccination}
            canOverrideVaccine={canOverrideVaccine}
            onVaccinationRecorded={handleVaccinationRecorded}
            onOverrideGranted={handleOverrideGranted}
            onOverrideMinStay={() => {
              setOverrideMinStay(true);
              setSubmitFailure(null);
            }}
            onOverrideAssessment={() => {
              setOverrideAssessment(true);
              setSubmitFailure(null);
            }}
            overrideMinStay={overrideMinStay}
            overrideAssessment={overrideAssessment}
            onSubmit={submit}
            onGoToStep={setStep}
          />
        )}
      </div>

      {/* Navigation */}
      {step < TOTAL_STEPS && (
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : navigate('/today'))}
            className="flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground press"
          >
            <ArrowLeft className="size-4" strokeWidth={1.75} />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          <div className="flex min-w-0 flex-col gap-1.5 sm:items-end">
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canProceedFromStep(step)}
              className="flex min-h-11 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {step === 2 ? 'Review booking' : 'Continue'}
              <ArrowRight className="size-4" strokeWidth={1.75} />
            </button>
            {blocked && <p className="text-xs text-muted-foreground sm:text-right">{blocked}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Booking — customer, service type, dates
// ---------------------------------------------------------------------------

const SERVICE_TYPE_OPTIONS: Array<{ value: ServiceType; label: string; description: string }> = [
  { value: 'boarding', label: 'Boarding', description: 'Overnight or multi-night stay' },
  { value: 'daycare', label: 'Daycare', description: 'Day visits' },
  { value: 'grooming', label: 'Grooming', description: 'Appointment with a groomer' },
];

function Step1Booking(props: {
  customer: Customer | null;
  onCustomerChange: (c: Customer | null) => void;
  preloadError: string | null;
  serviceType: ServiceType;
  onServiceTypeChange: (t: ServiceType) => void;
  checkInDate: string;
  checkInTime: string;
  checkoutDate: string;
  checkoutTime: string;
  onCheckInDateChange: (v: string) => void;
  onCheckInTimeChange: (v: string) => void;
  onCheckoutDateChange: (v: string) => void;
  onCheckoutTimeChange: (v: string) => void;
  facilityId: string;
  services: ServiceCatalogueItem[];
  groomers: Groomer[];
  groomingGroomerId: string | null;
  groomingStartAt: string;
  onGroomingSlotSelect: (groomerId: string | null, startAt: string) => void;
}) {
  const { customer, serviceType } = props;
  const isBoarding = serviceType === 'boarding';
  return (
    <div className="space-y-8">
      <section>
        <StepHeader number={1} title="Who and what" description="Customer, service and dates." />
        {props.preloadError && (
          <p className="mb-3 flex items-center gap-1.5 text-sm text-destructive">
            <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
            {props.preloadError}
          </p>
        )}
        <SectionLabel>Customer</SectionLabel>
        <CustomerCombobox value={customer} onChange={props.onCustomerChange} autoFocus />
      </section>

      <section>
        <SectionLabel>Service</SectionLabel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {SERVICE_TYPE_OPTIONS.map((opt) => {
            const active = serviceType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => props.onServiceTypeChange(opt.value)}
                aria-pressed={active}
                className={cn(
                  'rounded-xl border p-3 text-left transition-colors press',
                  active
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background hover:border-primary/40',
                )}
              >
                <p
                  className={cn('text-sm font-medium', active ? 'text-primary' : 'text-foreground')}
                >
                  {opt.label}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{opt.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        {serviceType === 'grooming' ? (
          <GroomingSlotPicker
            facilityId={props.facilityId}
            services={props.services}
            groomers={props.groomers}
            date={props.checkInDate}
            groomingGroomerId={props.groomingGroomerId}
            groomingStartAt={props.groomingStartAt}
            onDateChange={props.onCheckInDateChange}
            onSlotSelect={props.onGroomingSlotSelect}
          />
        ) : (
          <>
            <SectionLabel>{isBoarding ? 'Dates' : 'Date and time'}</SectionLabel>
            <div
              className={cn(
                'grid gap-4',
                isBoarding
                  ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                  : 'grid-cols-1 sm:grid-cols-2',
              )}
            >
              <DatePicker
                label="Check-in date"
                value={props.checkInDate}
                onChange={props.onCheckInDateChange}
              />
              <TimeField
                label="Check-in time"
                value={props.checkInTime}
                onChange={props.onCheckInTimeChange}
              />
              {isBoarding && (
                <>
                  <DatePicker
                    label="Checkout date"
                    value={props.checkoutDate}
                    onChange={props.onCheckoutDateChange}
                  />
                  <TimeField
                    label="Checkout time"
                    value={props.checkoutTime}
                    onChange={props.onCheckoutTimeChange}
                  />
                </>
              )}
            </div>
            {isBoarding &&
              props.checkInDate &&
              props.checkoutDate &&
              props.checkoutDate <= props.checkInDate && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-destructive">
                  <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
                  Checkout must be after check-in
                </p>
              )}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grooming slot picker (inside step 1 for grooming)
// ---------------------------------------------------------------------------

function GroomingSlotPicker({
  facilityId,
  services,
  groomers,
  date,
  groomingGroomerId,
  groomingStartAt,
  onDateChange,
  onSlotSelect,
}: {
  facilityId: string;
  services: ServiceCatalogueItem[];
  groomers: Groomer[];
  date: string;
  groomingGroomerId: string | null;
  groomingStartAt: string;
  onDateChange: (v: string) => void;
  onSlotSelect: (groomerId: string | null, startAt: string) => void;
}) {
  const [filterGroomerId, setFilterGroomerId] = useState<string>('');
  const [slots, setSlots] = useState<Record<string, GroomingSlot[]> | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const primaryService = services.find((s) => s.category === 'grooming' && s.isActive === 1);
  const durationMin = primaryService?.defaultDurationMin ?? 60;

  useEffect(() => {
    if (!facilityId || !date || !primaryService) {
      setSlots(null);
      return;
    }
    setSlotsLoading(true);
    listGroomingSlots({
      facilityId,
      date,
      durationMin,
      serviceId: primaryService.id,
      ...(filterGroomerId ? { groomerId: filterGroomerId } : {}),
    })
      .then((r) => setSlots(r.slots))
      .catch(() => setSlots({}))
      .finally(() => setSlotsLoading(false));
  }, [facilityId, date, durationMin, primaryService, filterGroomerId]);

  const flatSlots = useMemo((): Array<{
    groomerId: string;
    groomerName: string;
    slot: GroomingSlot;
  }> => {
    if (!slots) return [];
    const out: Array<{ groomerId: string; groomerName: string; slot: GroomingSlot }> = [];
    for (const [gId, slotList] of Object.entries(slots)) {
      const groomerName = groomers.find((g) => g.id === gId)?.displayName ?? gId.slice(-6);
      for (const slot of slotList) out.push({ groomerId: gId, groomerName, slot });
    }
    out.sort((a, b) => a.slot.startAt.localeCompare(b.slot.startAt));
    return out;
  }, [slots, groomers]);

  return (
    <div>
      <SectionLabel>Appointment slot</SectionLabel>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <DatePicker label="Appointment date" value={date} onChange={onDateChange} />
        {groomers.length > 0 && (
          <FormField label="Filter by groomer (optional)">
            <select
              value={filterGroomerId}
              onChange={(e) => {
                setFilterGroomerId(e.target.value);
                onSlotSelect(null, '');
              }}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            >
              <option value="">Any groomer</option>
              {groomers.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.displayName}
                </option>
              ))}
            </select>
          </FormField>
        )}
      </div>
      {!date ? (
        <p className="text-sm text-muted-foreground">Choose a date to see available slots.</p>
      ) : !primaryService ? (
        <p className="text-sm text-muted-foreground">
          No active grooming service found. Add one in Settings → Services first.
        </p>
      ) : slotsLoading ? (
        <p className="text-sm text-muted-foreground">Loading available slots…</p>
      ) : flatSlots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No slots available for this date. Try a different date or groomer filter.
        </p>
      ) : (
        <div>
          <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Available slots
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {flatSlots.map(({ groomerId, groomerName, slot }) => {
              const time = slot.startAt.slice(11, 16);
              const isSelected =
                groomingStartAt === slot.startAt && groomingGroomerId === groomerId;
              return (
                <button
                  key={`${groomerId}-${slot.startAt}`}
                  type="button"
                  onClick={() => onSlotSelect(groomerId, slot.startAt)}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-left text-xs transition-colors press',
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground hover:border-primary/40',
                  )}
                >
                  <p className="num font-semibold">{time}</p>
                  <p className="mt-0.5 truncate text-muted-foreground">{groomerName}</p>
                </button>
              );
            })}
          </div>
          {groomingStartAt && groomingGroomerId && (
            <p className="mt-3 text-sm font-medium text-success">
              Selected: {groomingStartAt.slice(11, 16)} with{' '}
              {groomers.find((g) => g.id === groomingGroomerId)?.displayName ?? 'groomer'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Pets — selection, per-pet details, vaccine gate, transport
// ---------------------------------------------------------------------------

function Step2Pets(props: {
  customer: Customer | null;
  pets: PetSlot[];
  onTogglePet: (pet: PetWithVaccinations) => void;
  onPatchPet: (petId: string, patch: Partial<PetSlot>) => void;
  serviceType: ServiceType;
  services: ServiceCatalogueItem[];
  facilityId: string;
  checkIn: string;
  checkout: string;
  visitDate: string;
  groomingGroomerId: string | null;
  groomers: Groomer[];
  vaccines: Vaccine[];
  policies: VaccinePolicy[];
  category: 'boarding' | 'grooming';
  canRecordVaccination: boolean;
  canOverrideVaccine: boolean;
  onVaccinationRecorded: (petId: string, summary: PetVaccinationSummary) => void;
  onOverrideGranted: (petId: string, vaccineId: string) => void;
  transport: TransportConfig;
  onTransportChange: (t: TransportConfig) => void;
  transportQuote: TransportQuote | null;
  transportQuoteLoading: boolean;
  transportFeeMinor: number;
  customerAddress: string;
  customerWhat3words: string;
  vehicles: Vehicle[];
  currency: string;
  facilityCountry: string | null;
}) {
  const noun = useSpaceNoun();
  const { pets, serviceType, services } = props;
  const isGrooming = serviceType === 'grooming';
  const assignedRunIds = pets.map((p) => p.runId).filter((id): id is string => id !== null);
  const groomerName = props.groomingGroomerId
    ? (props.groomers.find((g) => g.id === props.groomingGroomerId)?.displayName ?? null)
    : null;

  return (
    <div className="space-y-8">
      <section>
        <StepHeader
          number={2}
          title="Pets"
          description={
            isGrooming
              ? 'Tick the pet for this appointment.'
              : `Tick every pet that's coming. Each gets their own ${noun.singular}.`
          }
        />
        <PetMultiPicker
          customerId={props.customer?.id ?? null}
          selectedIds={pets.map((p) => p.petId)}
          onToggle={props.onTogglePet}
          single={isGrooming}
        />
      </section>

      {pets.length > 0 && (
        <section className="space-y-4">
          <SectionLabel>{isGrooming ? 'Appointment details' : 'Stay details'}</SectionLabel>
          {isGrooming && (
            <p className="text-sm text-muted-foreground">
              {groomerName
                ? `Groomer: ${groomerName}`
                : 'No groomer selected — assign one from the booking after saving.'}
            </p>
          )}
          {pets.map((slot) => {
            const petServices = services.filter((s) => {
              const allowed = safeJson<string[]>(s.speciesAllowed, []);
              if (!allowed.includes(slot.pet.species)) return false;
              const applicable = safeJson<Record<string, unknown>>(s.applicableTo, {});
              return applicable.boarding_addon !== false;
            });
            const otherAssignedRunIds = assignedRunIds.filter((id) => id !== slot.runId);
            return (
              <div key={slot.petId} className="rounded-xl border border-border p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{slot.pet.name}</span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    {slot.pet.species}
                  </span>
                  {slot.pet.sizeCategory && (
                    <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                      {slot.pet.sizeCategory}
                    </span>
                  )}
                </div>

                <div className="mb-3">
                  <VaccineRequirementsPanel
                    pet={slot.pet}
                    category={props.category}
                    policies={props.policies}
                    vaccines={props.vaccines}
                    visitDate={props.visitDate}
                    canRecord={props.canRecordVaccination}
                    canOverride={props.canOverrideVaccine}
                    onVaccinationRecorded={props.onVaccinationRecorded}
                    onOverrideGranted={props.onOverrideGranted}
                  />
                </div>

                {!isGrooming && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label={noun.Singular}>
                      <RunPicker
                        facilityId={props.facilityId}
                        value={slot.runId}
                        onChange={(runId) => props.onPatchPet(slot.petId, { runId })}
                        startsAt={props.checkIn || undefined}
                        endsAt={props.checkout || undefined}
                        excludeRunIds={otherAssignedRunIds}
                        species={slot.pet.species}
                      />
                    </FormField>
                  </div>
                )}
                <div className={cn('mt-4 grid grid-cols-1 gap-3', !isGrooming && 'md:grid-cols-2')}>
                  <FormField label={isGrooming ? 'Grooming notes' : 'Feeding instructions'}>
                    <textarea
                      value={slot.feeding}
                      onChange={(e) => props.onPatchPet(slot.petId, { feeding: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-border bg-surface p-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                    />
                  </FormField>
                  {!isGrooming && (
                    <FormField label="Medication">
                      <textarea
                        value={slot.medication}
                        onChange={(e) =>
                          props.onPatchPet(slot.petId, { medication: e.target.value })
                        }
                        rows={2}
                        className="w-full rounded-lg border border-border bg-surface p-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                      />
                    </FormField>
                  )}
                </div>
                {!isGrooming && petServices.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                      Optional add-on services
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {petServices.map((svc) => {
                        const selected = slot.selectedServiceIds.includes(svc.id);
                        return (
                          <button
                            key={svc.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                              props.onPatchPet(slot.petId, {
                                selectedServiceIds: selected
                                  ? slot.selectedServiceIds.filter((x) => x !== svc.id)
                                  : [...slot.selectedServiceIds, svc.id],
                              })
                            }
                            className={cn(
                              'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors press',
                              selected
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-background text-muted-foreground hover:border-primary/40',
                            )}
                          >
                            {svc.name}
                            <span className="num ml-1.5 opacity-70">
                              {formatPriceMinor(svc.defaultPriceMinor, props.currency)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {serviceType === 'daycare' && props.customer && pets.length > 0 && (
        <section>
          <SectionLabel>Repeat this visit</SectionLabel>
          <RecurringDaycarePanel
            customerId={props.customer.id}
            petIds={pets.map((p) => p.petId)}
            facilityId={props.facilityId}
            defaultDate={props.checkIn.slice(0, 10) || undefined}
          />
        </section>
      )}

      {!isGrooming && (
        <TransportSection
          transport={props.transport}
          onChange={props.onTransportChange}
          quote={props.transportQuote}
          quoteLoading={props.transportQuoteLoading}
          feeMinor={props.transportFeeMinor}
          customerAddress={props.customerAddress}
          customerWhat3words={props.customerWhat3words}
          vehicles={props.vehicles}
          currency={props.currency}
          facilityCountry={props.facilityCountry}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transport section (inside step 2)
// ---------------------------------------------------------------------------

export function describeTransportQuote(q: TransportQuote, currency: string): string {
  const via = q.destination.source === 'what3words' ? ' from the what3words square' : '';
  const miles =
    q.distanceMiles !== null ? `${q.distanceMiles.toFixed(1)} miles (straight line${via})` : null;
  switch (q.basis) {
    case 'unpriced':
      return 'No transport pricing set up — enter a fee below or configure Settings → Transport.';
    case 'free_radius':
      return `${miles ?? 'Within the free radius'} — no charge.`;
    case 'flat_fee':
      return `${q.legs} × ${formatPriceMinor(q.perLegMinor, currency)} flat fee per trip${miles ? ` · ${miles}` : ''}.`;
    case 'per_mile':
      return `${miles ?? 'Distance'} × per-mile rate = ${formatPriceMinor(q.perLegMinor, currency)} per trip, ${q.legs} trip${q.legs === 1 ? '' : 's'}.`;
    case 'distance_unknown':
      return `Distance could not be worked out — minimum fee of ${formatPriceMinor(q.perLegMinor, currency)} per trip applied.`;
    default:
      return '';
  }
}

const TRANSPORT_ISSUE_COPY: Record<string, string> = {
  customer_address_missing: 'The customer has no address on file — type one below.',
  customer_address_not_found: 'The address could not be located — check the postcode.',
  facility_address_missing:
    'The facility has no address set (Settings → Facilities), so distance cannot be calculated.',
  facility_address_not_found: 'The facility address could not be located on the map.',
  transport_not_configured: '',
  what3words_not_found:
    'That what3words address was not recognised — the postal address was used for distance instead.',
};

function TransportSection({
  transport,
  onChange,
  quote,
  quoteLoading,
  feeMinor,
  customerAddress,
  customerWhat3words,
  vehicles,
  currency,
  facilityCountry,
}: {
  transport: TransportConfig;
  onChange: (t: TransportConfig) => void;
  quote: TransportQuote | null;
  quoteLoading: boolean;
  feeMinor: number;
  customerAddress: string;
  customerWhat3words: string;
  vehicles: Vehicle[];
  currency: string;
  facilityCountry: string | null;
}) {
  const patch = (p: Partial<TransportConfig>) => onChange({ ...transport, ...p });
  const showPickup = transport.direction !== 'dropoff';
  const showDropoff = transport.direction !== 'pickup';
  const usingCustomer = transport.addressSource === 'customer';
  // Facility-address problems only matter when distance affects the price.
  const distanceMatters = quote?.pricingModel === 'per_mile' || quote?.basis === 'free_radius';
  const issues = (quote?.issues ?? [])
    .filter((i) => distanceMatters || !i.startsWith('facility_'))
    .map((i) => TRANSPORT_ISSUE_COPY[i] ?? '')
    .filter(Boolean);

  return (
    <section>
      <SectionLabel>Transport</SectionLabel>
      <label className="flex cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={transport.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
          className="size-4 rounded border-border"
        />
        <span className="text-sm font-medium">Add pickup / dropoff</span>
      </label>

      {!transport.enabled && (
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-dashed border-border p-3 text-muted-foreground">
          <Car className="size-5 shrink-0" strokeWidth={1.5} />
          <p className="text-sm">No transport — the customer brings and collects.</p>
        </div>
      )}

      {transport.enabled && (
        <div className="mt-4 space-y-4 rounded-xl border border-border p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Direction">
              <select
                value={transport.direction}
                onChange={(e) =>
                  patch({ direction: e.target.value as 'pickup' | 'dropoff' | 'both' })
                }
                className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none"
              >
                <option value="both">Pickup and dropoff</option>
                <option value="pickup">Pickup only (we collect)</option>
                <option value="dropoff">Dropoff only (we return)</option>
              </select>
            </FormField>
            {vehicles.length > 0 && (
              <FormField label="Vehicle (optional)">
                <select
                  value={transport.vehicleId}
                  onChange={(e) => patch({ vehicleId: e.target.value })}
                  className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none"
                >
                  <option value="">Unassigned</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                      {v.registration ? ` (${v.registration})` : ''}
                    </option>
                  ))}
                </select>
              </FormField>
            )}
          </div>

          {/* Address — the customer's, unless staff say otherwise */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Address
              </span>
              {usingCustomer && (customerAddress || customerWhat3words) && (
                <button
                  type="button"
                  onClick={() =>
                    patch({
                      addressSource: 'custom',
                      address: customerAddress,
                      what3words: customerWhat3words,
                    })
                  }
                  className="text-xs text-primary press"
                >
                  Use a different address
                </button>
              )}
              {!usingCustomer && (customerAddress || customerWhat3words) && (
                <button
                  type="button"
                  onClick={() => patch({ addressSource: 'customer', address: '', what3words: '' })}
                  className="text-xs text-primary press"
                >
                  Use the customer's address
                </button>
              )}
            </div>
            {usingCustomer && (customerAddress || customerWhat3words) ? (
              <div className="rounded-md border border-border bg-background px-3 py-2 text-sm">
                {customerAddress && <p>{customerAddress}</p>}
                {customerWhat3words && (
                  <p className="num">
                    <a
                      href={what3wordsMapUrl(customerWhat3words)}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium hover:underline"
                    >
                      {formatWhat3Words(customerWhat3words)}
                    </a>
                    <span className="ml-2 text-xs text-muted-foreground">exact square</span>
                  </p>
                )}
                <p className="text-xs text-muted-foreground">from the customer record</p>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={transport.address}
                  onChange={(e) => patch({ addressSource: 'custom', address: e.target.value })}
                  placeholder="123 Main Street, Town, AB1 2CD"
                  aria-label="Transport address"
                  className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none"
                />
                <What3WordsInput
                  value={transport.what3words}
                  onChange={(v) => patch({ addressSource: 'custom', what3words: v })}
                  country={facilityCountry}
                  aria-label="Transport what3words"
                  placeholder="what3words for the exact gate (optional)"
                />
              </div>
            )}
            {usingCustomer && !customerAddress && !customerWhat3words && (
              <p className="mt-1 text-xs text-muted-foreground">
                No address on the customer record — type one here (and add it to the customer later
                so it's remembered).
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {showPickup && (
              <FormField label="Pickup time">
                <input
                  type="datetime-local"
                  value={transport.pickupScheduledAt}
                  aria-label="Pickup time"
                  onChange={(e) => patch({ pickupScheduledAt: e.target.value })}
                  className="num h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none"
                />
              </FormField>
            )}
            {showDropoff && (
              <FormField label="Dropoff time">
                <input
                  type="datetime-local"
                  value={transport.dropoffScheduledAt}
                  aria-label="Dropoff time"
                  onChange={(e) => patch({ dropoffScheduledAt: e.target.value })}
                  className="num h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none"
                />
              </FormField>
            )}
          </div>

          {/* Fee — quoted automatically, overridable */}
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Transport fee
              </span>
              <span className="num text-base font-semibold" data-testid="transport-fee">
                {quoteLoading && transport.feeMode === 'auto' ? (
                  <Loader2 className="inline size-4 animate-spin" strokeWidth={1.75} />
                ) : (
                  formatPriceMinor(feeMinor, currency)
                )}
              </span>
            </div>
            {transport.feeMode === 'auto' ? (
              <>
                {quote && !quoteLoading && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {describeTransportQuote(quote, currency)}
                  </p>
                )}
                {issues.map((msg) => (
                  <p key={msg} className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 size-3 shrink-0" strokeWidth={2} />
                    {msg}
                  </p>
                ))}
                <button
                  type="button"
                  onClick={() => patch({ feeMode: 'manual', manualFeeMinor: String(feeMinor) })}
                  className="mt-2 text-xs text-primary press"
                >
                  Override fee
                </button>
              </>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={transport.manualFeeMinor}
                  onChange={(e) => patch({ manualFeeMinor: e.target.value })}
                  aria-label="Transport fee (minor units)"
                  className="num h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm outline-none"
                />
                <span className="text-xs text-muted-foreground">pence / cents, total</span>
                <button
                  type="button"
                  onClick={() => patch({ feeMode: 'auto' })}
                  className="text-xs text-primary press"
                >
                  Back to calculated fee
                </button>
              </div>
            )}
          </div>

          <FormField label="Driver notes (optional)">
            <textarea
              value={transport.notes}
              onChange={(e) => patch({ notes: e.target.value })}
              rows={2}
              placeholder={
                quote?.driverNotes ?? 'Gate code, access instructions, special handling…'
              }
              className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none"
            />
          </FormField>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Review + confirm
// ---------------------------------------------------------------------------

function Step3Review(props: {
  customer: Customer | null;
  pets: PetSlot[];
  serviceType: ServiceType;
  primaryService: ServiceCatalogueItem | undefined;
  checkIn: string;
  checkout: string;
  transport: TransportConfig;
  transportAddress: string;
  transportWhat3words: string;
  transportQuote: TransportQuote | null;
  transportFeeMinor: number;
  quote: Quote | null;
  perPetQuotes: Array<{ petName: string; quote: Quote }>;
  quoteLoading: boolean;
  quoteError: string | null;
  currency: string;
  groomingStartAt: string;
  groomingGroomerId: string | null;
  groomers: Groomer[];
  submitting: boolean;
  submitted: boolean;
  failure: SubmitFailure | null;
  petsFailingVaccines: PetSlot[];
  vaccines: Vaccine[];
  policies: VaccinePolicy[];
  category: 'boarding' | 'grooming';
  visitDate: string;
  canRecordVaccination: boolean;
  canOverrideVaccine: boolean;
  onVaccinationRecorded: (petId: string, summary: PetVaccinationSummary) => void;
  onOverrideGranted: (petId: string, vaccineId: string) => void;
  onOverrideMinStay: () => void;
  onOverrideAssessment: () => void;
  overrideMinStay: boolean;
  overrideAssessment: boolean;
  onSubmit: (sendToKiosk: boolean) => void;
  onGoToStep: (step: number) => void;
}) {
  const noun = useSpaceNoun();
  const {
    customer,
    pets,
    serviceType,
    primaryService,
    checkIn,
    checkout,
    transport,
    quote,
    currency,
    failure,
  } = props;
  const petCount = pets.length;
  const nightCount =
    serviceType === 'boarding' && checkIn && checkout
      ? Math.max(
          1,
          Math.floor(
            (new Date(checkout).getTime() - new Date(checkIn).getTime()) / (24 * 60 * 60 * 1000),
          ),
        )
      : null;

  const formatDt = (dt: string) => {
    try {
      return new Date(dt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return dt;
    }
  };

  const showPerPetBreakdown = props.perPetQuotes.length > 1;
  const grandTotal = (quote?.total_minor ?? 0) + props.transportFeeMinor;
  const canSave = Boolean(primaryService) && !props.submitting && !props.submitted;

  // Vaccine panels: the server's list when it rejected us, else the client mirror.
  const vaccinePanels: Array<{ slot: PetSlot; serverMissing: VaccineGateMissing[] | null }> =
    failure?.kind === 'vaccines'
      ? pets
          .filter((p) => failure.petId === null || p.petId === failure.petId)
          .slice(0, failure.petId === null ? 1 : undefined)
          .map((slot) => ({ slot, serverMissing: failure.missing }))
      : props.petsFailingVaccines.map((slot) => ({ slot, serverMissing: null }));

  return (
    <div>
      <StepHeader number={3} title="Review and save" description="Check the details, then save." />

      {vaccinePanels.length > 0 && (
        <div className="mb-5 space-y-3">
          {failure?.kind === 'vaccines' && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
              The booking was not saved — a required vaccination is missing. Fix it here and save
              again.
            </p>
          )}
          {vaccinePanels.map(({ slot, serverMissing }) => (
            <div key={slot.petId}>
              <p className="mb-1 text-sm font-medium">{slot.pet.name}</p>
              <VaccineRequirementsPanel
                pet={slot.pet}
                category={props.category}
                policies={props.policies}
                vaccines={props.vaccines}
                visitDate={props.visitDate}
                serverMissing={serverMissing}
                canRecord={props.canRecordVaccination}
                canOverride={props.canOverrideVaccine}
                onVaccinationRecorded={props.onVaccinationRecorded}
                onOverrideGranted={props.onOverrideGranted}
              />
            </div>
          ))}
        </div>
      )}

      {failure && failure.kind !== 'vaccines' && (
        <div className="mb-5 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
          <p className="flex items-start gap-1.5 text-destructive">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
            <span>
              {failure.kind === 'run_unavailable'
                ? `The ${noun.singular} chosen for ${
                    pets.find((p) => p.petId === failure.petId)?.pet.name ?? 'a pet'
                  } is no longer free for those dates.`
                : failure.kind === 'run_species_mismatch'
                  ? `The ${noun.singular} chosen for ${
                      pets.find((p) => p.petId === failure.petId)?.pet.name ?? 'a pet'
                    } does not take ${failure.species ?? 'that species'}s.`
                  : failure.message}
            </span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(failure.kind === 'run_unavailable' || failure.kind === 'run_species_mismatch') && (
              <button
                type="button"
                onClick={() => props.onGoToStep(2)}
                className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium press"
              >
                Pick another
              </button>
            )}
            {failure.kind === 'min_stay' && failure.overridable && (
              <button
                type="button"
                onClick={props.onOverrideMinStay}
                className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium press"
              >
                Override minimum stay (audit-logged)
              </button>
            )}
            {failure.kind === 'assessment' && failure.overridable && (
              <button
                type="button"
                onClick={props.onOverrideAssessment}
                className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium press"
              >
                Override evaluation requirement (audit-logged)
              </button>
            )}
            {(failure.kind === 'min_stay' || failure.kind === 'assessment') &&
              !failure.overridable && (
                <button
                  type="button"
                  onClick={() => props.onGoToStep(1)}
                  className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium press"
                >
                  Change dates
                </button>
              )}
          </div>
        </div>
      )}
      {(props.overrideMinStay || props.overrideAssessment) && (
        <p className="mb-4 text-xs text-amber-700">
          Saving with an override:{' '}
          {[props.overrideMinStay && 'minimum stay', props.overrideAssessment && 'evaluation']
            .filter(Boolean)
            .join(', ')}
          . This is recorded in the audit log.
        </p>
      )}

      <div className="space-y-4">
        <ReviewRow label="Customer">
          {customer ? `${customer.firstName} ${customer.lastName}` : '—'}
        </ReviewRow>
        <ReviewRow label="Service">
          <span className="capitalize">{serviceType}</span>
          {primaryService && (
            <span className="text-muted-foreground"> · {primaryService.name}</span>
          )}
        </ReviewRow>
        <ReviewRow label="Pets">
          <div className="flex flex-wrap gap-1.5">
            {pets.map((p) => (
              <span
                key={p.petId}
                className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium"
              >
                {p.pet.name} · {p.pet.species}
              </span>
            ))}
          </div>
        </ReviewRow>
        {serviceType === 'grooming' && props.groomingStartAt && (
          <ReviewRow label="Appointment">{formatDt(props.groomingStartAt)}</ReviewRow>
        )}
        {serviceType === 'grooming' && props.groomingGroomerId && (
          <ReviewRow label="Groomer">
            {props.groomers.find((g) => g.id === props.groomingGroomerId)?.displayName ??
              props.groomingGroomerId.slice(-6)}
          </ReviewRow>
        )}
        {serviceType !== 'grooming' && checkIn && (
          <ReviewRow label="Check-in">{formatDt(checkIn)}</ReviewRow>
        )}
        {serviceType !== 'grooming' && checkout && (
          <ReviewRow label="Checkout">{formatDt(checkout)}</ReviewRow>
        )}
        {nightCount !== null && (
          <ReviewRow label="Duration">
            {nightCount} night{nightCount !== 1 ? 's' : ''}
          </ReviewRow>
        )}
        {transport.enabled && (
          <ReviewRow label="Transport">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Truck className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
                <span className="font-medium">
                  {transport.direction === 'both'
                    ? 'Pickup and dropoff'
                    : transport.direction === 'pickup'
                      ? 'Pickup'
                      : 'Dropoff'}
                </span>
                <span className="num text-muted-foreground">
                  · {formatPriceMinor(props.transportFeeMinor, currency)}
                </span>
              </div>
              {props.transportAddress && (
                <p className="text-xs text-muted-foreground">{props.transportAddress}</p>
              )}
              {props.transportWhat3words && (
                <p className="num text-xs text-muted-foreground">
                  {formatWhat3Words(props.transportWhat3words)}
                </p>
              )}
              {transport.direction !== 'dropoff' && transport.pickupScheduledAt && (
                <p className="text-xs text-muted-foreground">
                  Pickup {formatDt(transport.pickupScheduledAt)}
                </p>
              )}
              {transport.direction !== 'pickup' && transport.dropoffScheduledAt && (
                <p className="text-xs text-muted-foreground">
                  Dropoff {formatDt(transport.dropoffScheduledAt)}
                </p>
              )}
              {props.transportQuote?.distanceMiles != null && (
                <p className="num text-xs text-muted-foreground">
                  {props.transportQuote.distanceMiles.toFixed(1)} miles from the facility (straight
                  line)
                </p>
              )}
            </div>
          </ReviewRow>
        )}
      </div>

      {/* Price estimate */}
      <div className="mt-6 rounded-xl border border-border bg-background p-4">
        <div className="mb-3 flex items-center gap-2">
          <ReceiptText className="size-4 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Price estimate
          </p>
        </div>

        {props.quoteLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
            Calculating…
          </div>
        )}
        {props.quoteError && !props.quoteLoading && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
            {props.quoteError}
          </p>
        )}
        {!primaryService && !props.quoteLoading && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30">
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
              strokeWidth={1.75}
            />
            <div>
              <p className="font-medium text-amber-900 dark:text-amber-200">
                No {serviceType} service configured
              </p>
              <p className="mt-0.5 text-amber-800 dark:text-amber-300">
                Go to{' '}
                <Link to="/settings/services" className="underline hover:no-underline">
                  Settings → Services
                </Link>{' '}
                to set up a {serviceType} rate before creating this booking.
              </p>
            </div>
          </div>
        )}

        {quote && !props.quoteLoading && (
          <div className="space-y-1.5">
            {showPerPetBreakdown && (
              <div className="mb-3 space-y-1 rounded-lg bg-secondary/40 px-3 py-2">
                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70">
                  Per pet
                </p>
                {props.perPetQuotes.map((ppq) => (
                  <div key={ppq.petName} className="flex items-baseline justify-between text-sm">
                    <span className="text-muted-foreground">{ppq.petName}</span>
                    <span className="num font-medium">
                      {formatPriceMinor(ppq.quote.total_minor, currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {serviceType === 'boarding' && nightCount !== null && primaryService
                  ? `${nightCount} night${nightCount !== 1 ? 's' : ''} × ${formatPriceMinor(primaryService.defaultPriceMinor, currency)}/night${petCount > 1 ? ` × ${petCount} pets` : ''}`
                  : serviceType === 'daycare' && primaryService
                    ? `${petCount} visit${petCount !== 1 ? 's' : ''} × ${formatPriceMinor(primaryService.defaultPriceMinor, currency)}/visit`
                    : primaryService
                      ? `${petCount > 1 ? `${petCount} appointments` : '1 appointment'} × ${formatPriceMinor(primaryService.defaultPriceMinor, currency)}`
                      : 'Base'}
              </span>
              <span className="num font-medium">
                {formatPriceMinor(quote.base_minor, currency)}
              </span>
            </div>
            {quote.total_minor !== quote.base_minor && (
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Pricing rules and discounts</span>
                <span className="num font-medium text-success">
                  {quote.total_minor < quote.base_minor ? '−' : '+'}
                  {formatPriceMinor(Math.abs(quote.total_minor - quote.base_minor), currency)}
                </span>
              </div>
            )}
            {transport.enabled && (
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Transport</span>
                <span className="num font-medium">
                  {formatPriceMinor(props.transportFeeMinor, currency)}
                </span>
              </div>
            )}
            <div className="border-t border-border pt-1.5" />
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">Total</span>
              <span className="num text-base font-semibold" data-testid="grand-total">
                {formatPriceMinor(grandTotal, currency)}
              </span>
            </div>
            {quote.payment_timing === 'deposit' && quote.deposit_minor > 0 && (
              <div className="mt-2 rounded-lg bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                Deposit due now:{' '}
                <span className="num font-medium text-foreground">
                  {formatPriceMinor(quote.deposit_minor, currency)}
                </span>
                {' · '}Balance of{' '}
                <span className="num font-medium text-foreground">
                  {formatPriceMinor(quote.balance_minor + props.transportFeeMinor, currency)}
                </span>{' '}
                due at {quote.balance_due_at ?? 'checkout'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => props.onSubmit(false)}
          disabled={!canSave}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground press disabled:opacity-50"
        >
          {props.submitting && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Save booking
        </button>
        {serviceType !== 'grooming' && (
          <button
            type="button"
            onClick={() => props.onSubmit(true)}
            disabled={!canSave}
            className="flex-1 rounded-lg border border-border bg-surface px-4 py-3 text-sm font-medium press disabled:opacity-50"
          >
            Save and send to kiosk
          </button>
        )}
        <button
          type="button"
          onClick={() => props.onGoToStep(2)}
          disabled={props.submitting}
          className="flex items-center justify-center gap-1.5 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Back
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function safeJson<T>(raw: string | null | undefined, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function StepHeader({
  number,
  title,
  description,
}: {
  number: number;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <p className="mb-1 text-[10.5px] font-medium uppercase tracking-[0.18em] text-muted-foreground/60">
        Step {number} of {TOTAL_STEPS}
      </p>
      <h2 className="font-heading text-2xl font-medium tracking-tight">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
      {children}
    </p>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function ReviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-4 border-b border-border pb-3 last:border-0">
      <p className="w-24 shrink-0 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground/70 sm:w-28">
        {label}
      </p>
      <div className="min-w-0 flex-1 text-sm">{children}</div>
    </div>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormField label={label}>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
      />
    </FormField>
  );
}
